/**
 * BackfillPreviewModal — Stories SCH-3.1 + SCH-3.2.
 *
 * Two operating modes inside one modal:
 *   - "preview" (SCH-3.1): read-only dry-run projection of SCH projects
 *     through configured mappings; admin verifies before committing.
 *   - "progress" (SCH-3.2): live batch-progress display once the admin
 *     clicks "Looks good — start backfill (50 at a time)". Polls
 *     /sync-progress every 3s; renders Continue / Resume / Done CTAs
 *     gated to admins + superadmins via useCanRunBackfill().
 *
 * Mode transitions:
 *   - Default: opens in preview mode (existing SCH-3.1 surface).
 *   - On "Looks good" click → useStartBackfill mutation runs → on success,
 *     mode flips to "progress" with the returned sync_run_id wired in.
 *   - When parent passes initialSyncRunId (e.g., from a Resume click),
 *     modal opens directly in progress mode, skipping preview.
 *   - On modal close, all state resets so the next open starts clean.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { AlertTriangle, Dices, Loader2, RefreshCw, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import { useBackfillPreview } from '@/hooks/integrations/useBackfillPreview';
import {
  useCanRunBackfill,
  useStartBackfill,
} from '@/hooks/integrations/useBackfillProgress';
import type {
  AdderSnapshotItem,
  BackfillPreviewResponse,
  MappingWarning,
  ProjectPreview,
} from '@/types/integrations/backfillPreview';
import { BackfillProgressView } from './BackfillProgressView';

interface BackfillPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * If provided, modal opens directly in progress mode for this run
   * (skips preview entirely). Used by the Resume banner click-through
   * and the SCH-3.5 "View last run" entry point.
   */
  initialSyncRunId?: string;
  /**
   * SCH-3.5: when true, the progress view is rendered with all CTAs hidden
   * (Continue / Resume / Done) — used for "View last run" on a completed run.
   */
  readOnly?: boolean;
}

const ADMIN_REQUIRED_TOOLTIP =
  "Administrator access is required to start a backfill.";

export function BackfillPreviewModal({
  isOpen,
  onClose,
  initialSyncRunId,
  readOnly = false,
}: BackfillPreviewModalProps) {
  // SCH-3.5: when a child component opens a ProjectModal (via the
  // openProjectModal custom event used app-wide), temporarily hide this
  // Dialog so the project takes visual focus. Restore when the project
  // closes via `projectModalClosed` (dispatched by FollowedProjectsModal's
  // close handler). State is preserved — closing the project drops the
  // user back into the drill-down view they were on.
  const [isProjectOverlayOpen, setIsProjectOverlayOpen] = useState(false);
  useEffect(() => {
    const handleOpen = () => setIsProjectOverlayOpen(true);
    const handleClose = () => setIsProjectOverlayOpen(false);
    window.addEventListener('openProjectModal', handleOpen);
    window.addEventListener('closeProjectModal', handleClose);
    window.addEventListener('projectModalClosed', handleClose);
    return () => {
      window.removeEventListener('openProjectModal', handleOpen);
      window.removeEventListener('closeProjectModal', handleClose);
      window.removeEventListener('projectModalClosed', handleClose);
    };
  }, []);
  const mutation = useBackfillPreview();
  const { mutate, reset, isPending, data, error } = mutation;
  const startBackfillMutation = useStartBackfill();
  const { canRun: canRunBackfill, loading: roleLoading } = useCanRunBackfill();
  const { toast } = useToast();

  // Modal mode — preview by default; progress when a sync_run_id is set
  // (either via initialSyncRunId from a Resume click or after the
  // start-backfill mutation succeeds).
  const [activeSyncRunId, setActiveSyncRunId] = useState<string | null>(
    initialSyncRunId ?? null,
  );

  // Search input controlled by the modal. activeSearch is the term used
  // in the most-recent fetch — empty string means "stratified sample mode."
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  // SCH-3.9: auto-continue is now picked at the Looks-Good moment via two
  // explicit buttons (gated vs. run-to-completion) instead of a checkbox
  // toggle — clearer choice, no missed checkbox. State lives on the
  // button-click path; nothing to track between clicks.

  // Reset when the modal closes. Critically, do NOT auto-fetch on open —
  // SCH responses are slow (30s+) and the admin might want to go straight
  // to search, not waste a sample fetch they don't need.
  useEffect(() => {
    if (!isOpen) {
      reset();
      setSearchInput('');
      setActiveSearch('');
      setActiveSyncRunId(null);
      startBackfillMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // When parent passes initialSyncRunId on open (Resume flow), seed the
  // progress mode immediately.
  useEffect(() => {
    if (isOpen && initialSyncRunId) {
      setActiveSyncRunId(initialSyncRunId);
    }
  }, [isOpen, initialSyncRunId]);

  function handleLooksGood(opts: { autoContinue: boolean }) {
    if (startBackfillMutation.isPending) return;
    startBackfillMutation.mutate(
      { batchSize: 50, autoContinue: opts.autoContinue },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not start backfill',
              description: result.error.message,
              variant: 'destructive',
            });
            return;
          }
          if (result.data) {
            setActiveSyncRunId(result.data.syncRunId);
          }
        },
      },
    );
  }

  function handlePocImport() {
    if (startBackfillMutation.isPending) return;
    const previewProjects = data?.data?.projects ?? [];
    if (previewProjects.length === 0) return;
    const filter = previewProjects.map((p) => p.subhubId);
    startBackfillMutation.mutate(
      { batchSize: 5, subhubUuidFilter: filter },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not start POC import',
              description: result.error.message,
              variant: 'destructive',
            });
            return;
          }
          if (result.data) {
            setActiveSyncRunId(result.data.syncRunId);
          }
        },
      },
    );
  }

  // Convenience: the preview's project list (or empty array). Used by the
  // footer CTAs to gate enabled/disabled state.
  const previewProjects = data?.data?.projects ?? [];

  // In progress mode, render a different surface (no preview cards / search).
  const isProgressMode = activeSyncRunId !== null;

  function handleSampleClick() {
    setActiveSearch('');
    setSearchInput('');
    mutate(undefined);
  }

  function handleSearchSubmit() {
    const trimmed = searchInput.trim();
    if (!trimmed) return;
    setActiveSearch(trimmed);
    mutate({ search: trimmed });
  }

  function handleClearSearch() {
    setSearchInput('');
    // Don't auto-fire a sample on clear — admin can click "Sample 10 random"
    // explicitly if they want a new sample, or type a new search.
    setActiveSearch('');
    reset();
  }

  function handleRefresh() {
    if (activeSearch) {
      mutate({ search: activeSearch });
    } else {
      mutate(undefined);
    }
  }

  // Has any fetch been initiated yet? Used to decide between the
  // initial-state CTA and the result body.
  const hasFetched = isPending || data !== undefined || error !== null;

  // SCH-3.5: visually hide ourselves while a ProjectModal is open above us.
  // Keep our state intact so closing the project restores the drill-down.
  const effectiveOpen = isOpen && !isProjectOverlayOpen;

  return (
    <Dialog open={effectiveOpen} onOpenChange={(open) => !open && !isProjectOverlayOpen && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>
            {isProgressMode ? 'Backfill in progress' : 'Backfill Preview'}
          </DialogTitle>
          <DialogDescription>
            {isProgressMode
              ? 'Live progress for the active backfill run. Click Continue between batches; closing the modal does not stop the run.'
              : 'A read-only projection of SubContractorHub projects through your current mappings. No data is imported until you confirm.'}
          </DialogDescription>
        </DialogHeader>

        {isProgressMode && activeSyncRunId ? (
          <BackfillProgressView
            syncRunId={activeSyncRunId}
            onClose={onClose}
            readOnly={readOnly}
          />
        ) : (
          <>
            <_ActionBar
              searchValue={searchInput}
              onSearchChange={setSearchInput}
              onSearchSubmit={handleSearchSubmit}
              onClearSearch={handleClearSearch}
              onSampleClick={handleSampleClick}
              activeSearch={activeSearch}
              isPending={isPending}
            />

            <div className="py-4">
              {!hasFetched && <_InitialState />}
              {isPending && !data && <_LoadingState />}
              {!isPending && error && (
                <_GenericErrorState message={error.message} onRetry={handleRefresh} />
              )}
              {!error && data && (
                <_ResultBody
                  result={data}
                  onRetry={handleRefresh}
                  onRefresh={handleRefresh}
                  isRefreshing={isPending}
                  activeSearch={activeSearch}
                />
              )}
            </div>

            {/* flex-wrap so the four CTAs (Close + POC + gated + auto)
                stack onto a second row at narrow widths instead of
                spilling past the modal's right edge. */}
            <DialogFooter className="flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {/* POC: admin / superadmin only, only after preview returns data. */}
              {canRunBackfill && previewProjects.length > 0 && (
                <_PocImportCTA
                  count={previewProjects.length}
                  disabled={startBackfillMutation.isPending}
                  isPending={startBackfillMutation.isPending}
                  onClick={handlePocImport}
                />
              )}
              {/* SCH-3.9 (revised UX): two explicit Looks-Good buttons —
                  gated vs. run-to-completion. Replaces the easy-to-miss
                  checkbox; admin picks the run mode at click time. */}
              <_LooksGoodCTA
                mode="gated"
                disabled={
                  previewProjects.length === 0 ||
                  startBackfillMutation.isPending ||
                  !canRunBackfill ||
                  roleLoading
                }
                isPending={startBackfillMutation.isPending}
                canRun={canRunBackfill}
                roleLoading={roleLoading}
                onClick={() => handleLooksGood({ autoContinue: false })}
              />
              <_LooksGoodCTA
                mode="auto"
                disabled={
                  previewProjects.length === 0 ||
                  startBackfillMutation.isPending ||
                  !canRunBackfill ||
                  roleLoading
                }
                isPending={startBackfillMutation.isPending}
                canRun={canRunBackfill}
                roleLoading={roleLoading}
                onClick={() => handleLooksGood({ autoContinue: true })}
              />
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Action bar (sample + search side-by-side) ─────────────────────────────

function _ActionBar({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  onSampleClick,
  activeSearch,
  isPending,
}: {
  searchValue: string;
  onSearchChange: (v: string) => void;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
  onSampleClick: () => void;
  activeSearch: string;
  isPending: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 border-b pb-3 sm:flex-row sm:items-center">
      <Button
        size="sm"
        variant="outline"
        onClick={onSampleClick}
        disabled={isPending}
        className="gap-1.5 sm:order-2"
      >
        <Dices className="h-4 w-4" />
        Sample 10 random projects
      </Button>
      <div className="relative flex-1 sm:order-1">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSearchSubmit();
            }
          }}
          placeholder="Search a project by name…"
          disabled={isPending}
          className="pl-8 pr-8"
        />
        {activeSearch && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={onClearSearch}
            disabled={isPending}
            className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <Button
        size="sm"
        onClick={onSearchSubmit}
        disabled={!searchValue.trim() || isPending}
        className="sm:order-1"
      >
        Search
      </Button>
    </div>
  );
}

// ─── Initial state (no fetch yet) ──────────────────────────────────────────

function _InitialState() {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <p className="text-sm text-muted-foreground max-w-md">
        Pick a way to start: sample 10 random projects across your SubContractorHub
        catalog, or search for a specific project by name.
      </p>
      <p className="text-xs text-muted-foreground/80">
        Each preview takes up to ~30 seconds while SubContractorHub responds.
      </p>
    </div>
  );
}

// ─── State sub-components ─────────────────────────────────────────────────

function _LoadingState() {
  return (
    <div className="flex flex-col items-center gap-3 py-12">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Generating preview…</p>
    </div>
  );
}

function _GenericErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <AlertTriangle className="h-8 w-8 text-destructive" />
      <p className="text-sm text-muted-foreground">{message}</p>
      <Button size="sm" variant="outline" onClick={onRetry} className="gap-2">
        <RefreshCw className="h-3 w-3" />
        Try again
      </Button>
    </div>
  );
}

function _ResultBody({
  result,
  onRetry,
  onRefresh,
  isRefreshing,
  activeSearch,
}: {
  result:
    | { data: BackfillPreviewResponse; error: null }
    | {
        data: null;
        error: {
          code:
            | 'auth_failed'
            | 'integration_disabled'
            | 'integration_not_found'
            | 'sch_unreachable'
            | 'internal_error';
          message: string;
        };
      };
  onRetry: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  activeSearch: string;
}) {
  if (result.error) {
    return <_BackendErrorState error={result.error} onRetry={onRetry} />;
  }

  const response = result.data;
  // SCH-unreachable surfaced in the data payload (vs. as a top-level error code).
  if (response.error === 'sch_unreachable') {
    return (
      <_BackendErrorState
        error={{ code: 'sch_unreachable', message: 'Could not reach SubContractorHub.' }}
        onRetry={onRetry}
      />
    );
  }

  if (response.projects.length === 0) {
    if (activeSearch) {
      return (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No projects in SubContractorHub match{' '}
          <span className="font-medium text-foreground">"{activeSearch}"</span>.
          Try a different search term.
        </div>
      );
    }
    return (
      <div className="space-y-3 py-8 text-center">
        <p className="text-sm text-muted-foreground">
          SubContractorHub returned no projects in this sample.
        </p>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={isRefreshing}>
          <RefreshCw className={`mr-2 h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
          Try a different sample
        </Button>
      </div>
    );
  }

  return (
    <_SuccessCards
      response={response}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      activeSearch={activeSearch}
    />
  );
}

function _BackendErrorState({
  error,
  onRetry,
}: {
  error: {
    code:
      | 'auth_failed'
      | 'integration_disabled'
      | 'integration_not_found'
      | 'sch_unreachable'
      | 'internal_error';
    message: string;
  };
  onRetry: () => void;
}) {
  const copy = _errorCopyFor(error.code, error.message);
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <AlertTriangle className="h-8 w-8 text-destructive" />
      <div>
        <p className="text-sm font-medium">{copy.title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{copy.body}</p>
      </div>
      {copy.canRetry && (
        <Button size="sm" variant="outline" onClick={onRetry} className="gap-2">
          <RefreshCw className="h-3 w-3" />
          Try again
        </Button>
      )}
    </div>
  );
}

function _errorCopyFor(
  code: string,
  fallbackMessage: string,
): { title: string; body: string; canRetry: boolean } {
  switch (code) {
    case 'auth_failed':
      return {
        title: 'SubContractorHub rejected your API key',
        body: 'Re-validate your key from the Connection tab, then try again.',
        canRetry: false,
      };
    case 'integration_disabled':
      return {
        title: 'Integration is disabled',
        body: 'Enable the integration before previewing.',
        canRetry: false,
      };
    case 'integration_not_found':
      return {
        title: 'No SubContractorHub integration is configured',
        body: 'Connect an account from Settings → Integrations first.',
        canRetry: false,
      };
    case 'sch_unreachable':
      return {
        title: "Couldn't reach SubContractorHub",
        body: 'This usually clears up on its own. Try again in a moment.',
        canRetry: true,
      };
    default:
      return {
        title: 'Something went wrong',
        body: fallbackMessage || 'Please try again.',
        canRetry: true,
      };
  }
}

// ─── Success body: cards + warnings ───────────────────────────────────────

function _SuccessCards({
  response,
  onRefresh,
  isRefreshing,
  activeSearch,
}: {
  response: BackfillPreviewResponse;
  onRefresh: () => void;
  isRefreshing: boolean;
  activeSearch: string;
}) {
  const generatedRelative = formatDistanceToNow(new Date(response.generatedAt), {
    addSuffix: true,
  });
  const refreshLabel = activeSearch ? 'Re-run search' : 'Load another sample';
  const showCount = activeSearch
    ? `${response.projects.length} match${response.projects.length === 1 ? '' : 'es'} for "${activeSearch}"`
    : `Showing ${response.projects.length} of ${response.totalFetched} projects`;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {showCount} • Generated {generatedRelative}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-7 gap-1.5 text-xs"
        >
          <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
          {refreshLabel}
        </Button>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {response.projects.map((p) => (
          <_PreviewCard key={p.subhubId} preview={p} />
        ))}
      </Accordion>
    </div>
  );
}

function _PreviewCard({ preview }: { preview: ProjectPreview }) {
  const { projected, mappingWarnings } = preview;
  return (
    <AccordionItem value={preview.subhubId} className="rounded-md border bg-card">
      <AccordionTrigger className="px-4 hover:no-underline">
        <div className="flex w-full items-center justify-between gap-3 pr-2">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <span className="w-full truncate text-left text-sm font-medium" title={preview.subhubName}>
              {preview.subhubName}
            </span>
            <span className="w-full truncate text-left text-xs text-muted-foreground">
              {projected.stage
                ? `→ ${projected.stage.pipelineName} / ${projected.stage.name}`
                : 'No target stage'}
            </span>
          </div>
          {mappingWarnings.length > 0 && (
            <Badge variant="outline" className="flex-shrink-0 border-yellow-400 bg-yellow-50 text-yellow-900">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {mappingWarnings.length}
            </Badge>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <_ProjectionDetails preview={preview} />
        {mappingWarnings.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Mapping warnings
            </p>
            {mappingWarnings.map((w, i) => (
              <_WarningRow key={i} warning={w} />
            ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function _ProjectionDetails({ preview }: { preview: ProjectPreview }) {
  const { projected } = preview;
  const grouped = _groupMappedFields(projected.equipment);

  return (
    <div className="space-y-4 text-sm">
      {/* Project header — always-present top-level fields */}
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
        <_HeaderRow label="SCH ID" value={preview.subhubId} mono />
        <_HeaderRow label="Project name" value={projected.projectName} />
        <_HeaderRow label="Address" value={projected.address} />
        <_HeaderRow
          label="Owner user"
          value={projected.ownerUserId ?? '(unresolved)'}
          mono={!!projected.ownerUserId}
          muted={!projected.ownerUserId}
        />
        <_HeaderRow label="Documents" value={`${projected.documentCount} allowed`} />
      </div>

      {/* Mapped fields — grouped by parent checklist */}
      {grouped.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Mapped fields
          </p>
          {grouped.map((group) => (
            <_MappingGroup key={group.heading} group={group} />
          ))}
        </div>
      )}

      {projected.addersSnapshot.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Adders
          </p>
          <div className="space-y-1 rounded border bg-muted/30 p-2">
            {projected.addersSnapshot.map((a, i) => (
              <_AdderRow key={i} adder={a} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Mapped-field grouping + rendering ─────────────────────────────────────

interface _MappingGroupData {
  heading: string;          // "Site Survey" | "Project columns" | "Other"
  rows: { label: string; value: unknown }[];
}

/**
 * Parse equipment dict keys (server sends "Template: Item" for checklist
 * targets, plain column name for project_column targets) and bucket them
 * by parent checklist for cleaner rendering.
 */
function _groupMappedFields(
  equipment: Record<string, unknown>,
): _MappingGroupData[] {
  const groups = new Map<string, { label: string; value: unknown }[]>();
  for (const [key, value] of Object.entries(equipment)) {
    const sepIdx = key.indexOf(': ');
    if (sepIdx > 0) {
      const heading = key.slice(0, sepIdx);
      const itemLabel = key.slice(sepIdx + 2);
      if (!groups.has(heading)) groups.set(heading, []);
      groups.get(heading)!.push({ label: itemLabel, value });
    } else {
      // Top-level project column (no template prefix).
      const heading = 'Project columns';
      if (!groups.has(heading)) groups.set(heading, []);
      groups.get(heading)!.push({ label: key, value });
    }
  }
  return Array.from(groups.entries()).map(([heading, rows]) => ({
    heading,
    rows,
  }));
}

function _MappingGroup({ group }: { group: _MappingGroupData }) {
  return (
    <div className="rounded-md border bg-muted/30">
      <div className="border-b bg-muted/40 px-3 py-1.5">
        <p className="text-xs font-semibold text-foreground/80">{group.heading}</p>
      </div>
      <div className="divide-y divide-border/50">
        {group.rows.map((row, i) => (
          <_MappingRow key={`${row.label}-${i}`} label={row.label} value={row.value} />
        ))}
      </div>
    </div>
  );
}

function _MappingRow({ label, value }: { label: string; value: unknown }) {
  const formatted = _formatValue(value);
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 px-3 py-1.5 text-sm">
      <span className="truncate text-foreground/80" title={label}>
        {label}
      </span>
      <span
        className={`min-w-0 break-all text-right font-mono text-xs ${
          isEmpty ? 'italic text-muted-foreground' : 'text-foreground'
        }`}
      >
        {formatted}
      </span>
    </div>
  );
}

/** Best-effort value formatter — pretty dates, booleans, numbers; otherwise stringified. */
function _formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    // ISO 8601 detection — match "YYYY-MM-DD" or "YYYY-MM-DDTHH:..." prefixes.
    if (/^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/.test(value)) {
      try {
        const parsed = parseISO(value);
        if (!Number.isNaN(parsed.getTime())) {
          // Time-of-day suffix when the original carried one; date-only otherwise.
          const hasTime = value.length > 10 && /\d{2}:\d{2}/.test(value);
          return format(parsed, hasTime ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy');
        }
      } catch {
        // fall through to raw string
      }
    }
    return value;
  }
  return String(value);
}

// ─── Header rows (top-level project fields) ────────────────────────────────

function _HeaderRow({
  label,
  value,
  mono,
  muted,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <>
      <span className="whitespace-nowrap text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={`min-w-0 ${mono ? 'break-all font-mono text-xs' : 'break-words'} ${muted ? 'italic text-muted-foreground' : ''}`}
      >
        {value ?? '(empty)'}
      </span>
    </>
  );
}

function _AdderRow({ adder }: { adder: AdderSnapshotItem }) {
  const total = adder.totalCostSnapshot;
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="min-w-0 truncate" title={adder.nameSnapshot}>{adder.nameSnapshot}</span>
      <span className="flex-shrink-0 text-xs text-muted-foreground">
        {adder.quantity ?? '?'} × {total !== null ? `$${total.toFixed(2)}` : '(no total)'}
      </span>
    </div>
  );
}

function _WarningRow({ warning }: { warning: MappingWarning }) {
  return (
    <div className="flex items-start gap-2 rounded border border-yellow-200 bg-yellow-50 p-2 text-xs">
      <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0 text-yellow-700" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="break-words text-yellow-900">{warning.message}</p>
        {(warning.sourceField || warning.targetField) && (
          <p className="break-all text-[11px] text-yellow-800/80">
            Source: <span className="font-mono">{warning.sourceField ?? '—'}</span>
            {' → '}Target: <span className="font-mono">{warning.targetField ?? '—'}</span>
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Footer CTAs ──────────────────────────────────────────────────────────

function _PocImportCTA({
  count,
  disabled,
  isPending,
  onClick,
}: {
  count: number;
  disabled: boolean;
  isPending: boolean;
  onClick: () => void;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="secondary"
            onClick={onClick}
            disabled={disabled}
            className="gap-2"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Test import ({count})
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          Imports just the {count} previewed projects in 2 batches of 5 — useful
          for verifying end-to-end behavior on a small sample before committing
          to the full catalog. You can delete them with a SQL cleanup afterwards.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function _LooksGoodCTA({
  mode,
  disabled,
  isPending,
  canRun,
  roleLoading,
  onClick,
}: {
  // SCH-3.9 (revised UX): two explicit button variants instead of a
  // checkbox modifier. `gated` = pause every 50 for a Continue click;
  // `auto` = chain batches until the catalog is exhausted.
  mode: 'gated' | 'auto';
  disabled: boolean;
  isPending: boolean;
  canRun: boolean;
  roleLoading: boolean;
  onClick: () => void;
}) {
  const label = mode === 'auto' ? 'Run until done' : 'Run 50 at a time';
  const hoverHelp =
    mode === 'auto'
      ? 'Chains all batches until the catalog is exhausted — no pauses, no Continue clicks.'
      : 'Imports the next 50 projects, then pauses for you to click Continue.';
  const button = (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant={mode === 'auto' ? 'default' : 'outline'}
      className="gap-2"
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : null}
      {label}
    </Button>
  );

  // Authorized (admin / superadmin): wrap the active button in a hover tooltip
  // so the short label keeps its meaning. Role-loading: render directly
  // (button is disabled via the disabled prop with a generic loading state).
  if (canRun) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent className="max-w-xs">{hoverHelp}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (roleLoading) {
    return button;
  }

  // No admin access (lacks manage_settings / system.override): disabled button
  // + explanatory tooltip. Gate matches the Settings → Integrations permission
  // set; see useCanRunBackfill().
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper allows tooltip on a disabled button */}
          <span tabIndex={0}>
            <Button disabled className="cursor-not-allowed gap-2 opacity-60">
              {label}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {ADMIN_REQUIRED_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
